export type AnalyticsCardPlacement = {
	position: 'bottom-start' | 'left-start';
	offset: [number, number];
};

export const DEFAULT_ANALYTICS_CARD_PLACEMENT: AnalyticsCardPlacement = {
	position: 'bottom-start',
	offset: [0, 8],
};

const DIALOG_CARD_GAP = 8;
const FALLBACK_PROFILE_CARD_WIDTH = 360;
const PIN_ATTRIBUTE = 'data-analytics-dialog-card-pin';
const INNER_PIN_ATTRIBUTE = 'data-analytics-dialog-card-pin-inner';
const TOP_VAR = '--analytics-dialog-card-top';
const LEFT_VAR = '--analytics-dialog-card-left';
const PIN_STYLE_ID = 'analytics-dialog-card-pin-styles';
const PENDING_STYLE_ID = 'analytics-dialog-card-pending-styles';

const PIN_STYLES = `[${PIN_ATTRIBUTE}]{position:fixed!important;top:var(${TOP_VAR})!important;left:var(${LEFT_VAR})!important;right:auto!important;bottom:auto!important;transform:none!important;visibility:visible!important;pointer-events:auto!important;}[${INNER_PIN_ATTRIBUTE}]{position:relative!important;top:auto!important;left:auto!important;right:auto!important;bottom:auto!important;transform:none!important;visibility:visible!important;}`;

const PROFILE_CARD_TRIGGER_SELECTORS = [
	'[data-testid="user-profile-card-trigger-wrapper"]',
	'[data-testid="profileCardTrigger"]',
];

export const getDialogBesideCardPlacement = (
	dialogRect?: Pick<DOMRect, 'left' | 'width' | 'height'> | null,
	triggerRect?: Pick<DOMRect, 'left'> | null,
): AnalyticsCardPlacement => {
	if (!dialogRect || !triggerRect || (dialogRect.width === 0 && dialogRect.height === 0)) {
		return DEFAULT_ANALYTICS_CARD_PLACEMENT;
	}
	return {
		position: 'left-start',
		offset: [0, triggerRect.left - dialogRect.left + DIALOG_CARD_GAP],
	};
};

const cssEscape = (value: string): string => {
	if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
		return CSS.escape(value);
	}
	return value.replace(/([^\w-])/g, '\\$1');
};

const ensurePinStyles = (): void => {
	if (typeof document !== 'undefined') {
		if (document.getElementById(PIN_STYLE_ID)) {
			return;
		}
		const style = document.createElement('style');
		style.id = PIN_STYLE_ID;
		style.textContent = PIN_STYLES;
		document.head.append(style);
	}
};

const writePendingHideStyles = (popupIds: string[]): void => {
	if (typeof document !== 'undefined') {
		ensurePinStyles();
		let style = document.getElementById(PENDING_STYLE_ID);
		if (!style) {
			style = document.createElement('style');
			style.id = PENDING_STYLE_ID;
			document.head.append(style);
		}
		style.textContent = popupIds
			.flatMap((popupId) => {
				const selector = `#${cssEscape(popupId)}:not([${PIN_ATTRIBUTE}]):not([${INNER_PIN_ATTRIBUTE}])`;
				return [
					`${selector}{visibility:hidden!important;pointer-events:none!important;}`,
					`*:has(>${selector}){visibility:hidden!important;pointer-events:none!important;}`,
				];
			})
			.join('');
	}
};

const isProfileCardTrigger = (node: HTMLElement): boolean =>
	PROFILE_CARD_TRIGGER_SELECTORS.some(
		(selector) => node.closest(selector) != null || node.querySelector(selector) != null,
	);

const findExpandedProfileCardTrigger = (trigger: HTMLElement): HTMLElement | null => {
	const expanded = trigger.matches('[aria-expanded="true"]')
		? trigger
		: trigger.querySelector('[aria-expanded="true"]');
	if (!(expanded instanceof HTMLElement) || !isProfileCardTrigger(expanded)) {
		return null;
	}
	return expanded;
};

const findOpenCardPopup = (expanded: HTMLElement): HTMLElement | null => {
	if (typeof document !== 'undefined') {
		const popupId = expanded.getAttribute('aria-controls');
		const popup = popupId ? document.getElementById(popupId) : null;
		return popup instanceof HTMLElement ? popup : null;
	}
	return null;
};

const applyPin = (node: HTMLElement, top: string, left: string): void => {
	node.setAttribute(PIN_ATTRIBUTE, 'true');
	node.style.setProperty(TOP_VAR, top);
	node.style.setProperty(LEFT_VAR, left);
};

const applyInnerPin = (node: HTMLElement): void => {
	node.setAttribute(INNER_PIN_ATTRIBUTE, 'true');
};

const isPinSafe = (node: HTMLElement): boolean => {
	if (typeof document !== 'undefined') {
		return (
			node !== document.body &&
			node !== document.documentElement &&
			!node.classList.contains('atlaskit-portal')
		);
	}
	return false;
};

const findPinRoot = (popup: HTMLElement): HTMLElement => {
	let root = popup;
	let node = popup.parentElement;
	while (node && isPinSafe(node) && node.childElementCount <= 1) {
		root = node;
		node = node.parentElement;
	}
	return root;
};

const isPinnedAt = (node: HTMLElement, top: string, left: string): boolean =>
	node.getAttribute(PIN_ATTRIBUTE) === 'true' &&
	node.style.getPropertyValue(TOP_VAR) === top &&
	node.style.getPropertyValue(LEFT_VAR) === left;

const collectExpandedProfileCardTriggers = (dialog: HTMLElement): HTMLElement[] => {
	const triggers: HTMLElement[] = [];
	for (const expanded of dialog.querySelectorAll('[aria-expanded="true"]')) {
		if (expanded instanceof HTMLElement && isProfileCardTrigger(expanded)) {
			triggers.push(expanded);
		}
	}
	return triggers;
};

const hideUnpinnedOpenCardPopups = (dialog: HTMLElement | null): void => {
	if (!dialog) {
		writePendingHideStyles([]);
		return;
	}
	writePendingHideStyles(
		collectExpandedProfileCardTriggers(dialog).flatMap((trigger) => {
			const popupId = trigger.getAttribute('aria-controls');
			return popupId ? [popupId] : [];
		}),
	);
};

export const getOpenAnalyticsDialogCardPopups = (dialog: HTMLElement | null): HTMLElement[] => {
	if (typeof document !== 'undefined') {
		if (!dialog) {
			return [];
		}
		const popups: HTMLElement[] = [];
		for (const trigger of collectExpandedProfileCardTriggers(dialog)) {
			const popup = findOpenCardPopup(trigger);
			if (!popup) {
				continue;
			}
			popups.push(popup);
			const root = findPinRoot(popup);
			if (root !== popup) {
				popups.push(root);
			}
		}
		return popups;
	}
	return [];
};

const pinExpandedCardBesideDialog = ({
	expanded,
	dialogLeft,
}: {
	expanded: HTMLElement;
	dialogLeft: number;
}): boolean => {
	const popup = findOpenCardPopup(expanded);
	if (!popup) {
		return false;
	}

	ensurePinStyles();

	const root = findPinRoot(popup);
	const cardWidth = popup.getBoundingClientRect().width || FALLBACK_PROFILE_CARD_WIDTH;
	const top = `${expanded.getBoundingClientRect().top}px`;
	const left = `${dialogLeft - cardWidth - DIALOG_CARD_GAP}px`;
	const innerPinned = (() => {
		let node: HTMLElement | null = popup;
		while (node && node !== root) {
			if (node.getAttribute(INNER_PIN_ATTRIBUTE) !== 'true') {
				return false;
			}
			node = node.parentElement;
		}
		return true;
	})();
	if (isPinnedAt(root, top, left) && innerPinned) {
		return true;
	}

	applyPin(root, top, left);
	let node: HTMLElement | null = popup;
	while (node && node !== root) {
		applyInnerPin(node);
		node = node.parentElement;
	}
	return true;
};

export const pinOpenCardBesideDialog = ({
	trigger,
	dialogLeft,
}: {
	trigger: HTMLElement;
	dialogLeft: number;
}): boolean => {
	const expanded = findExpandedProfileCardTrigger(trigger);
	if (!expanded) {
		return false;
	}

	const popupId = expanded.getAttribute('aria-controls');
	if (popupId) {
		writePendingHideStyles([popupId]);
	}

	return pinExpandedCardBesideDialog({ expanded, dialogLeft });
};

export const pinOpenCardsBesideDialog = (dialog: HTMLElement | null): boolean => {
	hideUnpinnedOpenCardPopups(dialog);
	if (!dialog) {
		return false;
	}
	const dialogLeft = dialog.getBoundingClientRect().left;
	let pinned = false;
	for (const trigger of collectExpandedProfileCardTriggers(dialog)) {
		pinned = pinExpandedCardBesideDialog({ expanded: trigger, dialogLeft }) || pinned;
	}
	return pinned;
};
